import { useCallback, useState } from 'react';

/**
 * useExport — PNG, CSV, and share-link export for any chart container.
 *
 * Usage:
 *   const ref = useRef<HTMLDivElement>(null);
 *   const { exportPNG, exportCSV, copyShareLink, copied } = useExport(ref, data, 'capa-trend');
 */
export function useExport(
  containerRef: React.RefObject<HTMLDivElement>,
  data: Record<string, unknown>[],
  chartId: string,
) {
  const [copied, setCopied] = useState(false);

  const exportPNG = useCallback(async () => {
    const container = containerRef.current;
    if (!container) return;
    const svgEl = container.querySelector('svg');
    if (!svgEl) return;

    // Clone SVG and inject white background
    const clone = svgEl.cloneNode(true) as SVGSVGElement;
    const bg = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
    bg.setAttribute('width', '100%');
    bg.setAttribute('height', '100%');
    bg.setAttribute('fill', '#ffffff');
    clone.insertBefore(bg, clone.firstChild);

    // Compute dimensions
    const bbox    = svgEl.getBoundingClientRect();
    const scale   = 2;
    const width   = (bbox.width  || 600) * scale;
    const height  = (bbox.height || 400) * scale;

    clone.setAttribute('width',  String(width));
    clone.setAttribute('height', String(height));

    const svgStr = new XMLSerializer().serializeToString(clone);
    const blob   = new Blob([svgStr], { type: 'image/svg+xml;charset=utf-8' });
    const url    = URL.createObjectURL(blob);

    const img = new Image();
    img.onload = () => {
      const canvas      = document.createElement('canvas');
      canvas.width      = width;
      canvas.height     = height;
      const ctx         = canvas.getContext('2d')!;
      ctx.fillStyle     = '#fff';
      ctx.fillRect(0, 0, width, height);
      ctx.drawImage(img, 0, 0);
      URL.revokeObjectURL(url);

      const a      = document.createElement('a');
      a.download   = `${chartId}-${Date.now()}.png`;
      a.href       = canvas.toDataURL('image/png');
      a.click();
    };
    img.src = url;
  }, [containerRef, chartId]);

  const exportCSV = useCallback(() => {
    if (!data?.length) return;
    const keys  = Object.keys(data[0]).filter((k) => !k.startsWith('_'));
    const rows  = [
      keys.join(','),
      ...data.map((row) =>
        keys
          .map((k) => {
            const v = row[k];
            if (v === null || v === undefined) return '';
            const s = String(v);
            return s.includes(',') || s.includes('"') ? `"${s.replace(/"/g, '""')}"` : s;
          })
          .join(','),
      ),
    ];
    const blob = new Blob([rows.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const a    = document.createElement('a');
    a.download = `${chartId}-${Date.now()}.csv`;
    a.href     = URL.createObjectURL(blob);
    a.click();
  }, [data, chartId]);

  const copyShareLink = useCallback(() => {
    navigator.clipboard.writeText(window.location.href).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, []);

  return { exportPNG, exportCSV, copyShareLink, copied };
}
